import React, { useState, useEffect } from 'react';
import axios from 'axios';


const Receipts = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter states
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchInvoiceNo, setSearchInvoiceNo] = useState('');
  const [searchInvoiceDate, setSearchInvoiceDate] = useState('');
  
  // Receipt modal states
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [receiptForm, setReceiptForm] = useState({
    amount: '',
    receipt_date: new Date().toISOString().split('T')[0],
    payment_mode: ''
  });
  const [receiptErrors, setReceiptErrors] = useState({});
  const [receiptSubmitting, setReceiptSubmitting] = useState(false);
  const [receiptSuccess, setReceiptSuccess] = useState('');

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Fetch invoices on component mount
  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiUrl}/api/receipts`);
      setInvoices(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter invoices based on search criteria
  const filteredInvoices = invoices.filter(invoice => {
    const customerMatch = invoice.customer?.name
      ?.toLowerCase()
      .includes(searchCustomer.toLowerCase());
    const invoiceNoMatch = invoice.invoice_no
      ?.toLowerCase()
      .includes(searchInvoiceNo.toLowerCase());
    const dateMatch = !searchInvoiceDate || invoice.invoice_date === searchInvoiceDate;

    return customerMatch && invoiceNoMatch && dateMatch;
  });

  // Calculate balance for an invoice
  const calculateBalance = (invoice) => {
    const total = parseFloat(invoice.ilines_sum_line_total) || 0;
    const paid = parseFloat(invoice.receipts_sum_amount) || 0;
    return total - paid;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Open receipt modal
  const openReceiptModal = (invoice) => {
    setSelectedInvoice(invoice);
    setReceiptForm({
      amount: '',
      receipt_date: new Date().toISOString().split('T')[0],
      payment_mode: ''
    });
    setReceiptErrors({});
    setReceiptSuccess('');
    setShowReceiptModal(true);
  };

  // Close receipt modal
  const closeReceiptModal = () => {
    setShowReceiptModal(false);
    setSelectedInvoice(null);
    setReceiptForm({
      amount: '',
      receipt_date: new Date().toISOString().split('T')[0],
      payment_mode: ''
    });
    setReceiptErrors({});
  };

  // Handle receipt form input change
  const handleReceiptFormChange = (field, value) => {
    setReceiptForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (receiptErrors[field]) {
      setReceiptErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate receipt form
  const validateReceiptForm = () => {
    const newErrors = {};
    const balance = calculateBalance(selectedInvoice);

    if (!receiptForm.amount) {
      newErrors.amount = 'Amount is required';
    } else {
      const amount = parseFloat(receiptForm.amount);
      if (amount <= 0) {
        newErrors.amount = 'Amount must be greater than 0';
      } else if (amount > balance) {
        newErrors.amount = `Amount cannot exceed balance (${formatCurrency(balance)})`;
      }
    }

    if (!receiptForm.receipt_date) {
      newErrors.receipt_date = 'Receipt date is required';
    }

    if (!receiptForm.payment_mode) {
      newErrors.payment_mode = 'Payment mode is required';
    }

    setReceiptErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit receipt
  const submitReceipt = async (e) => {
    e.preventDefault();

    if (!validateReceiptForm()) {
      return;
    }

    setReceiptSubmitting(true);

    try {
      const receiptData = {
        invoice_id: selectedInvoice.id,
        amount: parseFloat(receiptForm.amount),
        receipt_date: receiptForm.receipt_date,
        payment_mode: receiptForm.payment_mode
      };

      const response = await axios.post(`${apiUrl}/api/receipts`, receiptData);

      setReceiptSuccess('Receipt added successfully! ✅');

      // Refresh invoices
      setTimeout(() => {
        fetchInvoices();
        closeReceiptModal();
      }, 1500);
    } catch (err) {
      console.error('Error submitting receipt:', err);
      setReceiptErrors({
        submit: err.response?.data?.message || 'Failed to add receipt. Please try again.'
      });
    } finally {
      setReceiptSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="text-lg text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#23A19B] rounded-t-2xl p-4 sm:p-8 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">Receipts Management</h1>
              <p className="text-xs sm:text-base text-white/80">Manage and track invoice payments</p>
            </div>
            <button
              onClick={fetchInvoices}
              className="btn btn-sm sm:btn-md bg-white/20 text-white border-0 hover:bg-white/30 shrink-0"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-base-100 shadow-xl p-4 sm:p-6 border-t-4 border-primary">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Search by Customer</span>
              </label>
              <input
                type="text"
                placeholder="Customer name..."
                className="input input-bordered focus:border-primary"
                value={searchCustomer}
                onChange={(e) => setSearchCustomer(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Search by Invoice No.</span>
              </label>
              <input
                type="text"
                placeholder="Invoice number..."
                className="input input-bordered focus:border-primary"
                value={searchInvoiceNo}
                onChange={(e) => setSearchInvoiceNo(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Filter by Date</span>
              </label>
              <input
                type="date"
                className="input input-bordered focus:border-primary"
                value={searchInvoiceDate}
                onChange={(e) => setSearchInvoiceDate(e.target.value)}
              />
            </div>
          </div>

          {(searchCustomer || searchInvoiceNo || searchInvoiceDate) && (
            <div className="mt-4">
              <button
                onClick={() => {
                  setSearchCustomer('');
                  setSearchInvoiceNo('');
                  setSearchInvoiceDate('');
                }}
                className="btn btn-sm btn-outline"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mt-4">
            <span>{error}</span>
            <button onClick={fetchInvoices} className="btn btn-sm">
              Retry
            </button>
          </div>
        )}

        {/* Invoices Table */}
        <div className="bg-base-100 shadow-xl rounded-b-2xl overflow-hidden">
          {filteredInvoices.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-lg text-gray-500">No invoices found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                  <tr>
                    <th>Invoice No.</th>
                    <th>Customer</th>
                    <th>Invoice Date</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice, index) => {
                    const total = parseFloat(invoice.ilines_sum_line_total) || 0;
                    const paid = parseFloat(invoice.receipts_sum_amount) || 0;
                    const balance = total - paid;
                    const isFullyPaid = balance <= 0;

                    return (
                      <tr key={invoice.id} className="hover:bg-base-200 transition-colors">
                        <td className="font-bold text-primary">{invoice.invoice_no}</td>
                        <td>
                          <div className="flex flex-col">
                            <span className="font-semibold">{invoice.customer?.name}</span>
                            <span className="text-xs text-gray-500">{invoice.customer?.phone}</span>
                          </div>
                        </td>
                        <td>{formatDate(invoice.invoice_date)}</td>
                        <td>
                          <span className="font-mono font-bold">{formatCurrency(total)}</span>
                        </td>
                        <td>
                          <span className="font-mono text-success font-bold">{formatCurrency(paid)}</span>
                        </td>
                        <td>
                          <span
                            className={`font-mono font-bold ${
                              isFullyPaid ? 'text-success' : 'text-warning'
                            }`}
                          >
                            {formatCurrency(balance)}
                          </span>
                        </td>
                        <td>
                          <div
                            className={`badge ${
                              isFullyPaid
                                ? 'badge-success'
                                : invoice.status === 'Paid'
                                ? 'badge-primary'
                                : 'badge-warning'
                            }`}
                          >
                            {isFullyPaid ? 'Fully Paid' : invoice.status}
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => openReceiptModal(invoice)}
                            disabled={isFullyPaid}
                            className={`btn btn-sm ${
                              isFullyPaid
                                ? 'btn-disabled'
                                : 'btn-outline btn-primary hover:btn-primary'
                            }`}
                          >
                            💰 Add Receipt
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mt-4 text-center text-sm text-gray-600">
          <p>Showing {filteredInvoices.length} of {invoices.length} invoices</p>
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 space-y-6 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Add Receipt</h2>
              <p className="text-gray-600 text-sm mt-1">
                Invoice: {selectedInvoice.invoice_no}
              </p>
            </div>

            {/* Invoice Summary */}
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-4 rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-semibold">Invoice Total:</span>
                <span className="font-mono font-bold text-lg">
                  {formatCurrency(parseFloat(selectedInvoice.ilines_sum_line_total) || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-semibold">Payments Done:</span>
                <span className="font-mono font-bold text-success text-lg">
                  {formatCurrency(parseFloat(selectedInvoice.receipts_sum_amount) || 0)}
                </span>
              </div>
              <div className="divider my-2"></div>
              <div className="flex justify-between items-center">
                <span className="text-gray-900 font-bold">Balance Due:</span>
                <span
                  className={`font-mono font-bold text-lg ${
                    calculateBalance(selectedInvoice) <= 0
                      ? 'text-success'
                      : 'text-warning'
                  }`}
                >
                  {formatCurrency(calculateBalance(selectedInvoice))}
                </span>
              </div>
            </div>

            {/* Previous Receipts */}
            {selectedInvoice.receipts && selectedInvoice.receipts.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-sm text-gray-700 mb-2">Previous Receipts:</p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {selectedInvoice.receipts.map((receipt) => (
                    <div
                      key={receipt.id}
                      className="flex justify-between text-xs bg-white p-2 rounded border border-gray-200"
                    >
                      <span className="font-mono text-gray-600">{receipt.receipt_number}</span>
                      <span className="font-mono font-semibold text-primary">
                        {formatCurrency(receipt.amount)}
                      </span>
                      <span className="text-gray-500">{formatDate(receipt.receipt_date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Receipt Form */}
            <form onSubmit={submitReceipt} className="space-y-4">
              {/* Amount */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">
                    Amount * (Max: {formatCurrency(calculateBalance(selectedInvoice))})
                  </span>
                </label>
                <input
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={`input input-bordered focus:border-primary font-mono ${
                    receiptErrors.amount ? 'input-error' : ''
                  }`}
                  value={receiptForm.amount}
                  onChange={(e) => handleReceiptFormChange('amount', e.target.value)}
                  disabled={receiptSubmitting}
                />
                {receiptErrors.amount && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {receiptErrors.amount}
                    </span>
                  </label>
                )}
              </div>

              {/* Receipt Date */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Receipt Date *</span>
                </label>
                <input
                  type="date"
                  className={`input input-bordered focus:border-primary ${
                    receiptErrors.receipt_date ? 'input-error' : ''
                  }`}
                  value={receiptForm.receipt_date}
                  onChange={(e) =>
                    handleReceiptFormChange('receipt_date', e.target.value)
                  }
                  disabled={receiptSubmitting}
                />
                {receiptErrors.receipt_date && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {receiptErrors.receipt_date}
                    </span>
                  </label>
                )}
              </div>

              {/* Payment Mode */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Payment Mode *</span>
                </label>
                <select
                  className={`select select-bordered focus:border-primary ${
                    receiptErrors.payment_mode ? 'select-error' : ''
                  }`}
                  value={receiptForm.payment_mode}
                  onChange={(e) =>
                    handleReceiptFormChange('payment_mode', e.target.value)
                  }
                  disabled={receiptSubmitting}
                >
                  <option value="">Select Payment Mode</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank</option>
                  <option value="Cheque">Cheque</option>
                </select>
                {receiptErrors.payment_mode && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {receiptErrors.payment_mode}
                    </span>
                  </label>
                )}
              </div>

              {/* Submit Error */}
              {receiptErrors.submit && (
                <div className="alert alert-error">
                  <span>{receiptErrors.submit}</span>
                </div>
              )}

              {/* Success Message */}
              {receiptSuccess && (
                <div className="alert alert-success">
                  <span>{receiptSuccess}</span>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeReceiptModal}
                  className="btn btn-ghost flex-1"
                  disabled={receiptSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1 bg-gradient-to-r from-primary to-secondary border-0"
                  disabled={receiptSubmitting}
                >
                  {receiptSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Saving...
                    </>
                  ) : (
                    '💾 Add Receipt'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receipts;
